import React, { useEffect, useRef, useState } from 'react';
import { getLeaderboard, regenerateUpcoming, finishTournament, reopenTournament } from '../../api/tournamentsApi';
import { Button } from '../UI';
import { useToast } from '../Toast';
import Leaderboard from './Leaderboard';
import RoundCard from './RoundCard';
import ResultsExportModal from './ResultsExportModal';
import { hasExportableMatches } from './resultsExport';
import { IconShare, IconFlag, IconRefresh } from './TournamentIcons';

const RunningView = ({ tournament, canManage, sseLeaderboard, onTournamentUpdate, onEditTournament, onMatchSaved, onScoreModalOpen, onScoreModalClose, clientId }) => {
  const { addToast } = useToast();
  const [finishLoading, setFinishLoading]  = useState(false);
  const [reopenLoading, setReopenLoading]  = useState(false);
  const [regenLoading, setRegenLoading]   = useState(false);
  const [leaderboard, setLeaderboard]     = useState([]);
  const [exportOpen, setExportOpen]       = useState(false); // окно выгрузки результатов турнира

  const isRunning  = tournament.status === 'RUNNING';
  const isFinished = tournament.status === 'FINISHED';

  // Beat the Box: этап 2 активен, когда сгенерированы туры 4–6. В этом состоянии
  // основная таблица показывает ярусы этапа 2 (очки только этапа 2), а групповую
  // таблицу этапа 1 показываем отдельной свёрнутой панелью — как историю.
  const isBtb = tournament.format === 'BEAT_THE_BOX';
  const stage2Active = isBtb && (tournament.rounds || []).some(r => r.roundNumber > 3);
  const [stage1Leaderboard, setStage1Leaderboard] = useState([]);

  // Неравномерность сыгранных матчей среди активных игроков (из-за sit-out).
  // Рейтинг считается по сумме очков, поэтому сыгравшие больше матчей могут
  // оказаться выше «по объёму». Предупреждаем организатора перед завершением.
  const activeMatchesPlayed = (leaderboard || [])
    .filter(p => p.status === 'ACTIVE')
    .map(p => p.matchesPlayed ?? 0);
  const minPlayed = activeMatchesPlayed.length ? Math.min(...activeMatchesPlayed) : 0;
  const maxPlayed = activeMatchesPlayed.length ? Math.max(...activeMatchesPlayed) : 0;
  const unequalMatches = activeMatchesPlayed.length > 1 && maxPlayed !== minPlayed;

  // Хотя бы один раунд полностью завершён (статус раунда === FINISHED).
  const hasFullRound = (tournament.rounds || []).some(r => r.status === 'FINISHED');
  // Нет ни одного раунда с частично введёнными кортами (одни есть, другие ещё PENDING).
  // Если такой раунд есть — предупреждение ещё преждевременно.
  const hasPartialRound = (tournament.rounds || []).some(r => {
    const matches = r.matches || [];
    const pending = matches.filter(m => m.status === 'PENDING').length;
    return pending > 0 && pending < matches.length;
  });

  // Ref для измерения высоты фиксированной нижней панели — чтобы контент не прятался под ней.
  const bottomPanelRef = useRef(null);
  const [bottomPanelHeight, setBottomPanelHeight] = useState(80);

  // Строим map id → player для быстрого lookup
  const playerMap = Object.fromEntries((tournament.players || []).map(p => [p.id, p]));

  // Загружаем таблицу лидеров отдельным запросом
  const refreshLeaderboard = () => {
    getLeaderboard(tournament.publicId)
      .then(data => setLeaderboard(data || []))
      .catch(() => setLeaderboard([]));
  };

  useEffect(() => {
    refreshLeaderboard();
  }, [tournament.publicId, tournament.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // История этапа 1 (для BtB на этапе 2) — отдельным запросом ?stage=1.
  useEffect(() => {
    if (!stage2Active) { setStage1Leaderboard([]); return; }
    getLeaderboard(tournament.publicId, 1)
      .then(data => setStage1Leaderboard(data || []))
      .catch(() => setStage1Leaderboard([]));
  }, [stage2Active, tournament.publicId, tournament.updatedAt]);

  // SSE-лидерборд от TournamentPage: применяем сразу, без дополнительного запроса.
  // Покрывает случай когда tournament.updatedAt не меняется (например, очистка счёта).
  useEffect(() => {
    if (sseLeaderboard && sseLeaderboard.length > 0) {
      setLeaderboard(sseLeaderboard);
    }
  }, [sseLeaderboard]);

  useEffect(() => {
    const el = bottomPanelRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      setBottomPanelHeight(entries[0].contentRect.height + 16);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = () => {
    const host = window.location.origin;
    const url  = `${host}/public/tournaments/${tournament.publicToken}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
        .then(() => addToast('Ссылка скопирована', 'success'))
        .catch(() => addToast(url, 'info'));
    } else {
      addToast(url, 'info');
    }
  };

  const handleRegenerate = async () => {
    setRegenLoading(true);
    try {
      const updated = await regenerateUpcoming(tournament.publicId, clientId);
      onTournamentUpdate(updated);
      addToast('Расписание обновлено', 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleReopen = async () => {
    // Возврат в работу удаляет зафиксированную (замороженную) итоговую таблицу —
    // дальше места снова считаются из матчей текущим алгоритмом. Предупреждаем,
    // что при изменившемся с момента завершения алгоритме итог может разойтись.
    const finishedAt = tournament.updatedAt ? new Date(tournament.updatedAt) : null;
    let elapsedHint = '';
    if (finishedAt && !Number.isNaN(finishedAt.getTime())) {
      const ms = Date.now() - finishedAt.getTime();
      const days = Math.floor(ms / 86400000);
      const hours = Math.floor(ms / 3600000);
      const human = days >= 1 ? `${days} дн.` : hours >= 1 ? `${hours} ч.` : 'менее часа';
      elapsedHint = ` (${human})`;
    }
    const msg =
      'Вернуть турнир в работу?\n\n' +
      'Зафиксированная итоговая таблица лидеров будет удалена — места снова начнут ' +
      'считаться «вживую» из сыгранных матчей.\n\n' +
      'Если с момента завершения' + elapsedHint + ' изменился алгоритм подсчёта очков, ' +
      'при повторном завершении расстановка мест может получиться другой.';
    if (!window.confirm(msg)) return;
    setReopenLoading(true);
    try {
      const updated = await reopenTournament(tournament.publicId, clientId);
      onTournamentUpdate(updated);
      addToast('Турнир возобновлён', 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setReopenLoading(false);
    }
  };

  const handleFinish = async () => {
    let msg = 'Завершить турнир? Турнирная таблица будет зафиксирована.';
    if (unequalMatches) {
      msg += `\n\nВнимание: участники сыграли разное число матчей (${minPlayed}–${maxPlayed}). ` +
        'Места считаются по сумме набранных очков, поэтому сыгравшие больше матчей могут ' +
        'оказаться выше. При желании выровняйте число матчей, доиграв ещё тур.';
    }
    if (!window.confirm(msg)) return;
    setFinishLoading(true);
    try {
      const updated = await finishTournament(tournament.publicId, clientId);
      onTournamentUpdate(updated);
      addToast('Турнир завершён!', 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setFinishLoading(false);
    }
  };

  return (
    <>
      <div style={{ padding: `16px 16px ${bottomPanelHeight}px`, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Кнопка редактирования турнира (только RUNNING + canManage) */}
        {isRunning && canManage && (
          <button
            onClick={onEditTournament}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '13px 16px', borderRadius: 14,
              border: '1.5px solid #f0f0f5',
              background: '#fff',
              boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              cursor: 'pointer', width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: '#eef2ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  Редактировать турнир
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>
                  {(tournament.players || []).filter(p => p.status === 'ACTIVE').length} активных участников
                </div>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        )}

        {/* Баннер «Состав изменён — нужна регенерация» */}
        {isRunning && canManage && tournament.pendingRegeneration && (() => {
          // Есть ли хоть один матч не в статусе PENDING (т.е. уже начатые или завершённые раунды)?
          const hasCompletedRounds = (tournament.rounds || []).some(r =>
            (r.matches || []).some(m => m.status !== 'PENDING')
          );
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 14px', borderRadius: 12,
              background: '#fefce8', border: '1.5px solid #fde047',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#713f12' }}>
                  Состав изменён
                </div>
                <div style={{ fontSize: 12, color: '#854d0e', marginTop: 1 }}>
                  {hasCompletedRounds
                    ? 'Сначала очистите все сыгранные раунды, затем нажмите «Пересобрать»'
                    : 'Предстоящие раунды нужно пересобрать с учётом нового состава'}
                </div>
              </div>
              <button
                onClick={handleRegenerate}
                disabled={regenLoading || hasCompletedRounds}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: 8, flexShrink: 0,
                  border: '1.5px solid #fbbf24',
                  background: (regenLoading || hasCompletedRounds) ? '#fef9c3' : '#fef08a',
                  color: hasCompletedRounds ? '#a16207' : '#713f12',
                  fontSize: 12, fontWeight: 700,
                  cursor: (regenLoading || hasCompletedRounds) ? 'not-allowed' : 'pointer',
                  opacity: hasCompletedRounds ? 0.6 : 1,
                }}
              >
                <IconRefresh />
                {regenLoading ? 'Пересборка...' : 'Пересобрать раунды'}
              </button>
            </div>
          );
        })()}

        {/* Таблица лидеров (активный этап: у BtB на этапе 2 — ярусная, очки этапа 2) */}
        <Leaderboard
          players={leaderboard}
          rankingMode={tournament.rankingMode}
          hasCompletedRound={hasFullRound}
          title={stage2Active ? 'Таблица лидеров — этап 2' : 'Таблица лидеров'}
        />

        {/* Выгрузка результатов — доступна организатору, когда есть сыгранные матчи.
            Работает и в RUNNING, и в FINISHED; для BtB включает матчи обоих этапов. */}
        {canManage && hasExportableMatches(tournament) && (
          <button
            onClick={() => setExportOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '11px 16px', borderRadius: 12,
              border: '1.5px solid #fcd34d', background: '#fffbeb',
              color: '#b45309', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <IconShare />
            Скопировать результаты
          </button>
        )}

        {/* История этапа 1 — свёрнутая панель, доступна во время этапа 2 */}
        {stage2Active && stage1Leaderboard.length > 0 && (
          <Leaderboard
            players={stage1Leaderboard}
            rankingMode={tournament.rankingMode}
            hasCompletedRound
            title="Этап 1 — группы (история)"
            collapsible
            defaultCollapsed
          />
        )}

        {/* Подсказка для Mexicano: туры открываются инкрементально */}
        {isRunning && tournament.format === 'MEXICANO' && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '11px 14px', borderRadius: 12,
            background: '#f0f9ff', border: '1.5px solid #bae6fd',
          }}>
            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
            <div style={{ fontSize: 13, color: '#0369a1', lineHeight: 1.45 }}>
              <strong>Mexicano:</strong> следующий тур откроется после завершения текущего — пары будут сформированы по актуальному рейтингу.
            </div>
          </div>
        )}

        {/* Баннер итогов для завершённого турнира */}
        {isFinished && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 12,
            background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
            border: '1.5px solid #c4b5fd',
          }}>
            <span style={{ fontSize: 22 }}>🏆</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#5b21b6' }}>Турнир завершён</div>
              <div style={{ fontSize: 12, color: '#7c3aed' }}>
                {(tournament.rounds || []).length} раундов сыграно
              </div>
            </div>
            {canManage && (
              <button
                onClick={handleReopen}
                disabled={reopenLoading}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: '1.5px solid #c4b5fd',
                  background: '#fff', color: '#7c3aed',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {reopenLoading ? '...' : 'Вернуть в работу'}
              </button>
            )}
          </div>
        )}

        {/* Предупреждение о неравных матчах для завершённого турнира (без доп. условий) */}
        {isFinished && unequalMatches && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 12px', borderRadius: 10,
            background: '#fffbeb', border: '1.5px solid #fcd34d',
          }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
            <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.4 }}>
              Не все игроки сыграли одинаковое количество матчей
            </div>
          </div>
        )}

        {/* Раунды */}
        {(tournament.rounds || []).length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 14, padding: 20,
            border: '1.5px solid #f0f0f5',
            textAlign: 'center', color: '#9ca3af', fontSize: 13,
          }}>
            Раунды не найдены
          </div>
        ) : (
          (tournament.rounds || []).map(round => (
            <React.Fragment key={round.id}>
              {/* Плашка Beat the Box: перед первым раундом этапа 1 */}
              {tournament.format === 'BEAT_THE_BOX' && round.roundNumber === 1 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '11px 14px', borderRadius: 12,
                  background: '#f0f9ff', border: '1.5px solid #bae6fd',
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                  <div style={{ fontSize: 13, color: '#0369a1', lineHeight: 1.45 }}>
                    <strong>Beat the Box:</strong>{' '}
                    После завершения этапа 1 (раунды 1–3) группы будут пересобраны по результатам.
                  </div>
                </div>
              )}
              {/* Плашка Beat the Box: перед первым раундом этапа 2 (roundNumber > 3) */}
              {tournament.format === 'BEAT_THE_BOX' && round.roundNumber === 4 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '11px 14px', borderRadius: 12,
                  background: '#f0fdf4', border: '1.5px solid #86efac',
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                  <div style={{ fontSize: 13, color: '#15803d', lineHeight: 1.45 }}>
                    <strong>Beat the Box:</strong>{' '}
                    Этап 2 — Gold / Silver / Bronze / Crystal. Итог определяет лучший игрок группы Gold.
                  </div>
                </div>
              )}
              <RoundCard
                round={round}
                playerMap={playerMap}
                canEdit={canManage && isRunning}
                publicId={tournament.publicId}
                tournament={tournament}
                onScoreSaved={(updatedMatch) => {
                  onMatchSaved(updatedMatch);
                  // Дёргаем лидерборд через ~700мс — к этому моменту бэкенд уже пересчитал очки
                  setTimeout(refreshLeaderboard, 700);
                }}
                tournamentFinished={isFinished}
                onScoreModalOpen={onScoreModalOpen}
                onScoreModalClose={onScoreModalClose}
                clientId={clientId}
              />
            </React.Fragment>
          ))
        )}

      </div>

      {/* Фиксированная нижняя панель */}
      {isRunning && canManage && (
        <div ref={bottomPanelRef} style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          padding: '12px 16px',
          background: 'linear-gradient(to top, #f8fafc 70%, transparent)',
          maxWidth: 480, margin: '0 auto',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {/* Плашка: не все сыграли поровну (sit-out).
              Показываем только если хотя бы один раунд полностью завершён и нет частично введённых. */}
          {unequalMatches && hasFullRound && !hasPartialRound && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 12px', borderRadius: 10,
              background: '#fffbeb', border: '1.5px solid #fcd34d',
            }}>
              <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.4 }}>
                Не все игроки сыграли одинаковое количество матчей
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleShare}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '12px 18px', borderRadius: 12,
                border: '1.5px solid #d1d5db',
                background: '#fff', color: '#374151',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <IconShare />
              Публичная ссылка
            </button>
            <Button
              variant="danger"
              fullWidth
              loading={finishLoading}
              onClick={handleFinish}
            >
              <IconFlag /> Завершить турнир
            </Button>
          </div>
        </div>
      )}

      {/* Кнопка «Поделиться» для FINISHED */}
      {isFinished && (
        <div ref={bottomPanelRef} style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          padding: '12px 16px',
          background: 'linear-gradient(to top, #f8fafc 70%, transparent)',
          maxWidth: 480, margin: '0 auto',
        }}>
          <button
            onClick={handleShare}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px', borderRadius: 12,
              border: '1.5px solid #d1d5db',
              background: '#fff', color: '#374151',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <IconShare />
            Публичная ссылка
          </button>
        </div>
      )}

      {exportOpen && (
        <ResultsExportModal tournament={tournament} onClose={() => setExportOpen(false)} />
      )}
    </>
  );
};

export default RunningView;
